<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{ $t("bikeReceipt.pageTitle") }}</app-top-bar>

    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'">
      <v-container v-if="!showComplete">
        <h1 class="page-title mb-6 text-on-surface-variant">
          {{ $t("bikeReceipt.mainTitle") }}
        </h1>

        <p class="mb-6 text-on-surface-variant">
          {{ $t("bikeReceipt.description") }}
        </p>

        <v-textarea
          v-model="danfe"
          class="mb-4 text-on-surface-variant"
          inputmode="numeric"
          :label="$t('bikeReceipt.accessKeyLabel')"
          maxlength="54"
          variant="underlined"
          rows="3"
          type="tel"
          @keypress="onKeyPress($event)"
          @paste="onPaste"
          color="primary"
          background-color="surface"
        />
        <v-sheet class="text-center mt-10 text-on-surface-variant" v-if="countdown > 0">
          Aguarde, estamos processando a NFC-e... <strong>{{ countdown }}s </strong>
        </v-sheet>
        <v-img
          v-else
          class="mx-auto"
          contain
          max-width="400px"
          src="@/assets/images/danfe.png"
        />
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer>
      <continue-footer-button
        v-if="!showComplete"
        color="primary"
        :disabled="!isDanfeInputValid"
        :loading="isAdvancing"
        @click="advance"
      />
      <continue-footer-button
        v-if="showComplete"
        color="primary"
        :loading="isAdvancing"
        @click="advanceFlow"
      />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
import _ from "lodash";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { isValidDanfe, parseDanfe } from "@/composables/useDanfeUtils";
import { usePageBackground } from "@/composables/usePageBackground";
import { useStepFlow } from "@/composables/useStepFlow";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";
import { showToast } from "@/utils/toast";

const { t } = useI18n();

const route = useRoute();
const api = useApiStore();
const app = useAppStore();
const { mainStyle, hasBackgroundImage } = usePageBackground();
const pathPage = '/bike-receipt';

const { isAdvancing, showComplete, advance: advanceFlow } = useStepFlow(pathPage, () => isComplete.value);

const countdown = ref(0);
let countdownInterval: any = null;

const isComplete = computed(() => api.isItemComplete("receipt"));

const danfe = ref(api.bikeDANFECode ? formatDanfe(api.bikeDANFECode) : "");
const isDanfeInputValid = computed(
  () => danfe.value && danfe.value.length === 44 + 10
);

async function advance() {
  if (!validateDanfe(danfe.value)) {
    return;
  }

  const danfeCode = danfe.value.replace(/\s/g, "");
  api.bikeDANFECode = danfeCode;

  const parsed = parseDanfe(api.bikeDANFECode);
  if (parsed && parsed.mod === 65) {
    startCountdown();
  }

  if (route.params.dev === "true") {
    showToast.info({
      title: t("bikeReceipt.danfeTitle"),
      description: api.bikeDANFECode,
    });
  }

  try {
    await advanceFlow(async () => {
      await api.loadDanfe(danfeCode);
      await api.getData();
    });
    stopCountdown();
  } catch (error) {
    console.log(error);
    stopCountdown();
  }
}

function validateDanfe(value) {
  // Ex: NFC-e 43141219266800000187650010000000031098003421
  // Ex: NF-e 35201029071565000141550020000009101053606057
  const valid = isValidDanfe(value);
  const parsed = parseDanfe(value);
  if (!valid || !parsed) {
    showToast.warning({
      title: t("bikeReceipt.invalidKeyTitle"),
      description: t("bikeReceipt.invalidKeyDescription"),
    });
    return false;
  }
  if (parsed.mod !== 55 && parsed.mod !== 65) {
    showToast.warning({
      title: t("bikeReceipt.invalidModelTitle"),
      description: t("bikeReceipt.invalidModelDescription"),
    });
    return false;
  }
  return true;
}

function formatDanfe(value) {
  if (value == null) return null;

  const digits = value.replace(/\D/g, "");
  return _.chunk(digits, 4)
    .map((chunk) => chunk.join(""))
    .join(" ");
}

function onKeyPress(evt) {
  const keysAllowed = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
  const keyPressed = evt.key;

  if (!keysAllowed.includes(keyPressed)) {
    evt.preventDefault();
  }
}

function onPaste(data) {
  danfe.value = data.clipboardData.getData("text").replace(/ /g, "");
}

watch(danfe, (val) => {
  if (val == null) return;

  danfe.value = formatDanfe(val);
});

function startCountdown() {
  countdown.value = 60;
  countdownInterval = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--;
    } else {
      stopCountdown();
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  countdown.value = 0;
}
</script>

<style lang="scss" scoped>
.campaign-warning {
  color: #ed1c24;
  text-align: center;
}

:deep(.v-textarea textarea) {
  font-family: ui-monospace, "Cascadia Mono", Consolas, "Lucida Console",
    "Courier New", Courier, monospace;
  font-optical-sizing: auto;
  font-weight: 400;
  font-style: normal;
}
</style>
